"use strict";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNativeAddress } from "constants/uniswapAddresses";
import { AssetType } from "uniswapEntities";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { useMaxAmountSpend } from "features/gas/useMaxAmountSpend";
import { FeatureFlags } from "features/gating/flags";
import { useFeatureFlag } from "features/gating/hooks";
import { useSwapAnalytics } from "features/transactions/swap/analytics";
import { useDerivedSwapInfo } from "features/transactions/swap/contexts/hooks/useDerivedSwapInfo";
import { selectFilteredChainIds } from "features/transactions/swap/contexts/selectors";
import { updateFilteredChainIds } from "features/transactions/swap/contexts/slice";
import { TransactionType } from "features/transactions/types/transactionDetails";
import { CurrencyField } from "types/currency";
import { currencyId } from "utils/uniswapCurrencyId";
import { logContextUpdate } from "utilities/src/logger/contextEnhancer";
import { usePrevious } from "utilities/src/react/hooks";
import { useValueAsRef } from "utilities/src/react/useValueAsRef";
import { useDebounceWithStatus } from "utilities/src/time/timing";
const SWAP_FORM_DEBOUNCE_TIME_MS = 250;
function getDefaultInputCurrency(chainId) {
  return {
    address: getNativeAddress(chainId),
    chainId,
    type: AssetType.Currency
  };
}
export const getDefaultState = (defaultChainId) => ({
  exactAmountFiat: void 0,
  exactAmountToken: "",
  exactCurrencyField: CurrencyField.INPUT,
  focusOnCurrencyField: CurrencyField.INPUT,
  filteredChainIds: {},
  input: getDefaultInputCurrency(defaultChainId),
  output: void 0,
  isFiatMode: false,
  isMax: false,
  isSubmitting: false
});
export const SwapFormContext = createContext(void 0);
export function SwapFormContextProvider({
  children,
  hideFooter,
  hideSettings,
  prefilledState
}) {
  const amountUpdatedTimeRef = useRef(0);
  const exactAmountFiatRef = useRef("");
  const exactAmountTokenRef = useRef("");
  const { defaultChainId } = useEnabledChains();
  const dispatch = useDispatch();
  const persistedFilteredChainIds = useSelector(selectFilteredChainIds);
  const defaultState = useMemo(() => getDefaultState(defaultChainId), [defaultChainId]);
  const [swapForm, setSwapForm] = useState(prefilledState ?? defaultState);
  const datadogEnabled = useFeatureFlag(FeatureFlags.Datadog);
  const previousInitialInputCurrency = usePrevious(prefilledState?.input);
  const previousInitialOutputCurrency = usePrevious(prefilledState?.output);
  useEffect(() => {
    const previousInputCurrencyId = previousInitialInputCurrency && currencyId(previousInitialInputCurrency);
    const previousOutputCurrencyId = previousInitialOutputCurrency && currencyId(previousInitialOutputCurrency);
    const newInputCurrencyId = prefilledState?.input && currencyId(prefilledState.input);
    const newOutputCurrencyId = prefilledState?.output && currencyId(prefilledState.output);
    if (previousInputCurrencyId === newInputCurrencyId && previousOutputCurrencyId === newOutputCurrencyId) {
      return;
    }
    setSwapForm((currentForm) => {
      const currentFormInputId = currentForm.input && currencyId(currentForm.input);
      const currentFormOutputId = currentForm.output && currencyId(currentForm.output);
      if (currentFormInputId === newInputCurrencyId && currentFormOutputId === newOutputCurrencyId) {
        return currentForm;
      }
      return prefilledState ?? defaultState;
    });
  }, [prefilledState, previousInitialInputCurrency, previousInitialOutputCurrency, defaultState]);
  useEffect(() => {
    if (prefilledState?.selectingCurrencyField) {
      setSwapForm((oldVal) => {
        return {
          ...oldVal,
          selectingCurrencyField: prefilledState?.selectingCurrencyField,
          filteredChainIds: prefilledState.filteredChainIds,
          isSelectingCurrencyFieldPrefilled: true
        };
      });
    }
  }, [prefilledState?.selectingCurrencyField, prefilledState?.filteredChainIds]);
  const previousExactCurrencyField = usePrevious(swapForm.exactCurrencyField);
  const hasExactCurrencyFieldChanged = previousExactCurrencyField !== swapForm.exactCurrencyField;
  const [debouncedExactAmountToken, isDebouncingExactAmountToken] = useDebounceWithStatus({
    value: swapForm.exactAmountToken,
    delay: SWAP_FORM_DEBOUNCE_TIME_MS,
    skipDebounce: hasExactCurrencyFieldChanged
  });
  const [debouncedExactAmountFiat, isDebouncingExactAmountFiat] = useDebounceWithStatus({
    value: swapForm.exactAmountFiat,
    delay: SWAP_FORM_DEBOUNCE_TIME_MS,
    skipDebounce: hasExactCurrencyFieldChanged
  });
  const derivedSwapInfo = useDerivedSwapInfo({
    txId: swapForm.txId,
    [CurrencyField.INPUT]: swapForm.input ?? null,
    [CurrencyField.OUTPUT]: swapForm.output ?? null,
    exactCurrencyField: swapForm.exactCurrencyField,
    exactAmountToken: debouncedExactAmountToken ?? "",
    exactAmountFiat: debouncedExactAmountFiat,
    focusOnCurrencyField: swapForm.focusOnCurrencyField,
    selectingCurrencyField: swapForm.selectingCurrencyField,
    isDebouncing: isDebouncingExactAmountToken || isDebouncingExactAmountFiat
  });
  const inputAmount = derivedSwapInfo.currencyAmounts[CurrencyField.INPUT];
  const inputBalanceAmount = derivedSwapInfo.currencyBalances[CurrencyField.INPUT];
  useSwapAnalytics(derivedSwapInfo);
  const maxInputAmountAsRef = useValueAsRef(
    useMaxAmountSpend({
      currencyAmount: inputBalanceAmount,
      txType: TransactionType.Swap,
      isExtraTx: true
    })?.toExact()
  );
  useEffect(() => {
    const inputAmountString = inputAmount?.toExact();
    const maxInputAmountThresholdString = maxInputAmountAsRef.current;
    if (derivedSwapInfo.exactCurrencyField === CurrencyField.OUTPUT && inputAmountString && maxInputAmountThresholdString) {
      const isMaxThreshold = !!(parseFloat(inputAmountString) >= parseFloat(maxInputAmountThresholdString));
      setSwapForm(
        (prevState) => prevState.isMax === isMaxThreshold ? prevState : {
          ...prevState,
          isMax: isMaxThreshold
        }
      );
    }
  }, [inputAmount, derivedSwapInfo.exactCurrencyField, maxInputAmountAsRef]);
  const updateSwapForm = useCallback(
    (newState) => {
      let isAmountUpdated = false;
      if ("exactAmountFiat" in newState || "exactAmountToken" in newState) {
        amountUpdatedTimeRef.current = Date.now();
        isAmountUpdated = true;
      }
      if ("exactAmountFiat" in newState) {
        exactAmountFiatRef.current = newState.exactAmountFiat ?? "";
      }
      if ("exactAmountToken" in newState) {
        exactAmountTokenRef.current = newState.exactAmountToken ?? "";
      }
      if ("filteredChainIds" in newState) {
        dispatch(
          updateFilteredChainIds({ filteredChainIds: newState.filteredChainIds ?? persistedFilteredChainIds ?? {} })
        );
      }
      setSwapForm((prevState) => {
        const updatedState = { ...prevState, ...newState };
        if (isAmountUpdated || newState.exactCurrencyField !== CurrencyField.OUTPUT) {
          const isMaxTokenAmount = maxInputAmountAsRef.current && updatedState.exactAmountToken && parseFloat(maxInputAmountAsRef.current) <= parseFloat(updatedState.exactAmountToken);
          updatedState.isMax = newState.isMax ?? !!isMaxTokenAmount;
        }
        logContextUpdate("SwapFormContext", updatedState, datadogEnabled);
        return updatedState;
      });
    },
    // avoid rerenders unless absolutely necessary since this component is widely used
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSwapForm, datadogEnabled]
  );
  const state = useMemo(
    () => ({
      amountUpdatedTimeRef,
      derivedSwapInfo,
      exactAmountFiat: swapForm.exactAmountFiat,
      exactAmountFiatRef,
      exactAmountToken: swapForm.exactAmountToken,
      exactAmountTokenRef,
      exactCurrencyField: swapForm.exactCurrencyField,
      focusOnCurrencyField: swapForm.focusOnCurrencyField,
      filteredChainIds: swapForm.filteredChainIds,
      input: swapForm.input,
      isFiatMode: swapForm.isFiatMode,
      isMax: swapForm.isMax,
      presetPercentage: swapForm.presetPercentage,
      preselectAsset: swapForm.preselectAsset,
      isSubmitting: swapForm.isSubmitting,
      output: swapForm.output,
      selectingCurrencyField: swapForm.selectingCurrencyField,
      txId: swapForm.txId,
      hideFooter,
      hideSettings,
      updateSwapForm,
      prefilledCurrencies: [prefilledState?.input, prefilledState?.output].filter(
        (asset) => Boolean(asset)
      ),
      isSelectingCurrencyFieldPrefilled: swapForm.isSelectingCurrencyFieldPrefilled
    }),
    [
      derivedSwapInfo,
      swapForm.exactAmountFiat,
      swapForm.exactAmountToken,
      swapForm.exactCurrencyField,
      swapForm.focusOnCurrencyField,
      swapForm.filteredChainIds,
      swapForm.input,
      swapForm.isFiatMode,
      swapForm.isMax,
      swapForm.presetPercentage,
      swapForm.preselectAsset,
      swapForm.isSubmitting,
      swapForm.output,
      swapForm.selectingCurrencyField,
      swapForm.txId,
      swapForm.isSelectingCurrencyFieldPrefilled,
      hideFooter,
      hideSettings,
      updateSwapForm,
      prefilledState?.input,
      prefilledState?.output
    ]
  );
  return <SwapFormContext.Provider value={state}>{children}</SwapFormContext.Provider>;
}
export const useOptionalSwapFormContext = () => {
  return useContext(SwapFormContext);
};
export const useSwapFormContext = () => {
  const swapContext = useContext(SwapFormContext);
  if (swapContext === void 0) {
    throw new Error("`useSwapFormContext` must be used inside of `SwapFormContextProvider`");
  }
  return swapContext;
};
